import { useState, useMemo } from 'react'
import Markdown from 'react-markdown'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Copy, Check, ClipboardList, ChevronDown, ChevronUp } from 'lucide-react'
import { useStoryWriterStore } from '../store/storyWriterStore'

interface ParsedStory {
  title: string
  body: string
  raw: string
}

function parseStories(text: string): ParsedStory[] {
  const blocks = text.split(/\n---\n/).filter((b) => b.trim())

  return blocks.map((block) => {
    const trimmed = block.trim()
    const titleMatch = trimmed.match(/^###\s+(.+)/)
    const title = titleMatch ? titleMatch[1].trim() : 'User Story'
    const body = titleMatch ? trimmed.slice(titleMatch[0].length).trim() : trimmed

    return { title, body, raw: trimmed }
  })
}

function CopyButton({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Button
      size="sm"
      variant="ghost"
      onClick={(e) => {
        e.stopPropagation()
        void handleCopy()
      }}
      className="gap-1.5 h-7 text-xs text-muted-foreground hover:text-foreground"
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {label ?? (copied ? 'Copied!' : 'Copy')}
    </Button>
  )
}

const markdownComponents = {
  // h4 = section headers (#### User story, #### Acceptance Criteria, etc.)
  h4: ({ children }: { children?: React.ReactNode }) => (
    <div className="mt-5 mb-2 flex items-center gap-2 border-b border-border/50 pb-1.5 first:mt-0">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {children}
      </h4>
    </div>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="my-1.5 text-sm leading-relaxed text-foreground">{children}</p>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => {
    const text = String(children)
    // [tbd] markers
    if (text === '[tbd]') {
      return (
        <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-medium text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-600">
          tbd
        </Badge>
      )
    }
    // "important →" callouts
    if (text.toLowerCase().startsWith('important')) {
      return (
        <strong className="font-semibold text-amber-600 dark:text-amber-400">{children}</strong>
      )
    }
    return <strong className="font-semibold">{children}</strong>
  },
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="my-1.5 space-y-1.5 text-sm">{children}</ul>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li className="flex items-start gap-2 text-sm leading-relaxed">
      <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-primary/50" />
      <span className="flex-1">{children}</span>
    </li>
  ),
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline underline-offset-2 hover:text-primary/80 break-all"
    >
      {children}
    </a>
  ),
  table: ({ children }: { children?: React.ReactNode }) => (
    <div className="my-3 overflow-x-auto rounded-md border">
      <table className="w-full text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }: { children?: React.ReactNode }) => (
    <thead className="bg-muted/50 text-xs font-medium uppercase tracking-wider text-muted-foreground">
      {children}
    </thead>
  ),
  th: ({ children }: { children?: React.ReactNode }) => (
    <th className="px-3 py-2 text-left font-medium">{children}</th>
  ),
  td: ({ children }: { children?: React.ReactNode }) => (
    <td className="border-t px-3 py-2">{children}</td>
  ),
}

function StoryCard({ story, index }: { story: ParsedStory; index: number }) {
  const [collapsed, setCollapsed] = useState(false)

  return (
    <div className="rounded-lg border bg-card shadow-sm overflow-hidden">
      {/* Title bar */}
      <div
        className="flex items-center justify-between border-b bg-muted/40 px-4 py-2.5 cursor-pointer select-none"
        onClick={() => setCollapsed((c) => !c)}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-primary/10 text-primary">
            <ClipboardList className="h-3.5 w-3.5" />
          </div>
          <span className="text-xs font-medium text-muted-foreground tracking-wide uppercase shrink-0">
            Story {index + 1}
          </span>
          <span className="font-semibold text-sm truncate">{story.title}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <CopyButton text={story.raw} />
          {collapsed ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </div>

      {/* Body */}
      {!collapsed && (
        <div className="p-5">
          <Markdown components={markdownComponents}>{story.body}</Markdown>
        </div>
      )}
    </div>
  )
}

export function StoryOutput() {
  const { outputText, status, inputTokens, outputTokens } = useStoryWriterStore()
  const [copied, setCopied] = useState(false)

  const stories = useMemo(() => {
    if (!outputText) return []
    return parseStories(outputText)
  }, [outputText])

  if (!outputText) return null

  const handleCopyAll = async () => {
    await navigator.clipboard.writeText(outputText)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold">Generated Stories</h2>
          {stories.length > 0 && status !== 'streaming' && (
            <Badge variant="secondary" className="text-xs">
              {stories.length} {stories.length === 1 ? 'story' : 'stories'}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {inputTokens != null && outputTokens != null && (
            <Badge variant="outline" className="text-xs font-mono">
              {inputTokens + outputTokens} tokens
            </Badge>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => void handleCopyAll()}
            className="gap-1.5"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copied!' : 'Copy all'}
          </Button>
        </div>
      </div>

      {status === 'streaming' ? (
        <div className="rounded-lg border bg-muted/30 p-4 text-sm font-mono whitespace-pre-wrap leading-relaxed">
          {outputText}
          <span className="animate-pulse">▍</span>
        </div>
      ) : (
        <div className="grid gap-4">
          {stories.map((story, i) => (
            <StoryCard key={i} story={story} index={i} />
          ))}
        </div>
      )}
    </div>
  )
}
